import { Component, useState, type ErrorInfo, type ReactNode } from "react";
import { BrowserRouter, Link, Route, Routes, useNavigate } from "react-router-dom";

import { PlayerRoute } from "@client/app/player-route";
import { DisplayRoute } from "@client/app/display-route";
import { CaveAtmosphere } from "@client/components/cave-atmosphere";
import { RulesSheet } from "@client/components/rules-sheet";
import { JoinCodeSchema } from "@saboteur/contracts";
import { CardGallery } from "@client/features/cards/card-gallery";

type ErrorBoundaryProps = { children: ReactNode };
type ErrorBoundaryState = { error?: Error };

class RootErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {};

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Application render failed", { error, componentStack: info.componentStack });
  }

  public override render(): ReactNode {
    if (this.state.error) {
      return (
        <main className="centered-page" role="alert">
          <section className="surface stack">
            <p className="eyebrow">Something went wrong</p>
            <h1>The mine is temporarily blocked.</h1>
            <p>Refresh the page to reconnect to your room.</p>
            <button type="button" onClick={() => window.location.reload()}>
              Refresh
            </button>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

function Home(): ReactNode {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  return (
    <main className="cave-scene welcome-page">
      <CaveAtmosphere welcome />
      <section className="welcome-layout" aria-label="Welcome to Saboteur">
        <header className="welcome-title">
          <h1>Saboteur</h1>
          <p>Dig for gold. Trust no dwarf.</p>
        </header>
        <div className="home-controls">
          <nav className="button-row" aria-label="Get started">
            <Link className="button" to="/room/new">
              Create room
            </Link>
          </nav>
          <form
            className="home-join"
            onSubmit={(e) => {
              e.preventDefault();
              const parsed = JoinCodeSchema.safeParse(code);
              if (parsed.success) void navigate(`/room/${parsed.data}`);
              else setError("Enter the 6–8 letter or number code shared by your host.");
            }}
          >
            <label htmlFor="room-code">Room code</label>
            <div>
              <input
                id="room-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setError("");
                }}
                autoCapitalize="characters"
                maxLength={8}
                placeholder="ABC234"
                required
              />
              <button type="submit">Join room</button>
            </div>
            {error ? <p role="alert">{error}</p> : null}
          </form>
        </div>
      </section>
      <footer className="welcome-footer">
        <RulesSheet />
      </footer>
    </main>
  );
}

export function AppRouter(): ReactNode {
  return (
    <RootErrorBoundary>
      <BrowserRouter>
        <Routes>
          {import.meta.env.DEV ? <Route path="/dev/cards" element={<CardGallery />} /> : null}
          <Route path="/" element={<Home />} />
          <Route path="/room/:joinCode" element={<PlayerRoute />} />
          <Route path="/display/:displayCode" element={<DisplayRoute />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </RootErrorBoundary>
  );
}
